import { lt } from './types';
import type { Chapter } from './types';

/**
 * Help-Center content — one chapter per game system. Written to be read
 * standalone (a player can jump straight to "PvP-Kampf" from search without
 * reading anything before it), so each chapter repeats the handful of terms
 * it depends on rather than assuming an earlier chapter was read.
 */
export const CHAPTERS: Chapter[] = [
  {
    id: 'basics',
    title: lt('Erste Schritte', 'Getting started'),
    summary: lt(
      'Das HUD, die Ressourcenleiste und die Navigation im Überblick.',
      'The HUD, the resource bar and the navigation, at a glance.',
    ),
    sections: [
      {
        id: 'hud',
        heading: lt('Das Cockpit', 'The cockpit'),
        body: [
          lt(
            'Oben läuft die Ressourcenleiste — Titan, Silikat, Isotop, Energie und deine Speicherkapazität, live hochgezählt. Ein Klick auf einen Wert öffnet die Statistik dazu (Förderung pro Stunde, Verlauf).',
            'The resource bar runs across the top — titan, silicate, isotope, energy and your storage capacity, ticking up live. Click any value to open its stats (production per hour, history).',
          ),
          lt(
            'Links (bzw. unten als Leiste auf dem Handy) sitzt die Hauptnavigation zu allen Bereichen: Übersicht, Gebäude, Forschung, Werft, Abwehr, Sektor-Karte, Piratenlager, Händler, Casino, Shop, Nachrichten, Einstellungen. Ein Punkt mit Zahl zeigt eine laufende Warteschlange, ein rot pulsierendes Symbol eine dringende Bedrohung.',
            'The main navigation to every section lives on the left (or as a bottom bar on phones): Overview, Buildings, Research, Shipyard, Defense, Sector Map, Pirate Camps, Trade, Casino, Shop, Messages, Settings. A numbered dot marks a running queue, a pulsing red icon an urgent threat.',
          ),
          lt(
            'Rechts oben siehst du deinen Kommandanten-Level, deine Erfahrungspunkte bis zum nächsten Level und offene Talentpunkte — ein Klick öffnet den Talentbaum in der Forschungsansicht.',
            'Top right shows your commander level, your XP toward the next one, and unspent talent points — click it to open the talent tree inside the Research view.',
          ),
        ],
      },
      {
        id: 'goal',
        heading: lt('Worum geht es', 'What the game is about'),
        body: [
          lt(
            'Du baust einen Heimatplaneten aus: Rohstoffe fördern, Gebäude ausbauen, Forschung betreiben, eine Flotte aufstellen. Später kommen ein eigener Mond, weitere Kolonien in der Galaxie, Handel, Spionage und Kämpfe gegen Piraten, NPC-Kolonien und andere Spieler dazu.',
            'You build up a home planet: mine resources, expand buildings, research technology, build a fleet. Later a moon, further colonies across the galaxy, trading, espionage and combat against pirates, NPC colonies and other players join in.',
          ),
          lt(
            'Fast jede Aktion — bauen, forschen, ein Schiff bauen, eine Flotte losschicken — läuft über eine Warteschlange mit Bauzeit. Das Spiel läuft auch weiter, wenn du offline bist: Ressourcen laufen auf, Warteschlangen werden fertig, Verteidigung reagiert automatisch (siehe Kapitel "Abwehr & Ereignisse").',
            'Almost every action — building, researching, building a ship, sending a fleet — runs through a queue with a build time. The game keeps running while you\'re offline: resources keep accumulating, queues finish, defense reacts automatically (see the "Defense & Events" chapter).',
          ),
        ],
      },
      {
        id: 'onboarding',
        heading: lt('Die ersten Aufträge', 'The first missions'),
        body: [
          lt(
            'Eine Auftragskarte in der Seitenleiste führt dich anfangs durch die ersten Schritte (Titan-Schmelze bauen, Energie sichern, Forschungslabor errichten, erste Schiffe bauen …) und belohnt jeden Schritt mit Ressourcen oder Erfahrung. Sie verschwindet, sobald du sie durchlaufen hast — dieses Hilfe-Center bleibt danach dein dauerhaftes Nachschlagewerk.',
            'A mission card in the sidebar walks you through the first steps early on (build the titan smelter, secure power, put up a research lab, build your first ships …) and rewards each step with resources or XP. It disappears once you\'ve finished it — this Help Center stays as your permanent reference afterward.',
          ),
        ],
      },
    ],
  },
  {
    id: 'resources',
    title: lt('Ressourcen & Produktion', 'Resources & production'),
    summary: lt(
      'Titan, Silikat, Isotop, Energie, Lagerkapazität — wie Förderung wirklich berechnet wird.',
      'Titan, silicate, isotope, energy, storage — how production is actually calculated.',
    ),
    sections: [
      {
        id: 'kinds',
        heading: lt('Die Rohstoffarten', 'Resource types'),
        body: [
          lt(
            'Titan, Silikat und Isotop sind die Grundrohstoffe — jeder wird von einem eigenen Gebäude gefördert (Titan-Schmelze, Silikat-Raffinerie, Isotop-Kondensator) und braucht Energie, um mit voller Leistung zu laufen. Aether, Credits und Chronos-Kristalle sind Sondermengen: Aether kommt aus besonderen Quellen, Credits laufen über den Händler, Chronos-Kristalle sind die Premium-Währung des Shops.',
            'Titan, silicate and isotope are the base resources — each mined by its own building (titan smelter, silicate refinery, isotope condenser) and needs power to run at full output. Aether, credits and Chronos crystals are special-purpose: aether comes from special sources, credits run through the merchant, Chronos crystals are the shop\'s premium currency.',
          ),
        ],
      },
      {
        id: 'energy',
        heading: lt('Energie', 'Power'),
        body: [
          lt(
            'Das Solar-Gravitations-Kraftwerk erzeugt Energie, die Fördergebäude verbrauchen sie. Reicht die Energie nicht für alle Gebäude, drosselt das Spiel automatisch die Förderung anteilig — nichts steht komplett still, aber jedes Gebäude läuft unter seinem Maximum. Die Energiebilanz siehst du direkt in der Ressourcenleiste.',
            'The solar-gravitational plant generates power, mining buildings consume it. If power falls short for every building, the game automatically throttles production proportionally — nothing stops entirely, but every building runs below its maximum. You can see the power balance right in the resource bar.',
          ),
        ],
        tips: [
          lt(
            'Vor dem nächsten Ausbau eines Fördergebäudes lohnt sich ein Blick auf die Energie — ein weiteres Level bringt nichts, wenn am Ende sowieso gedrosselt wird.',
            'Before upgrading another mining building, check power first — another level does nothing if it just gets throttled anyway.',
          ),
        ],
      },
      {
        id: 'storage',
        heading: lt('Lagerkapazität', 'Storage'),
        body: [
          lt(
            'Titan-Depot, Silikat-Speicher und Isotop-Tank legen die Obergrenze für den jeweiligen Rohstoff fest. Ist ein Lager voll, läuft überschüssige Förderung ungenutzt ins Leere — die Ressourcenleiste warnt mit einer eigenen Farbe, wenn ein Lager fast voll ist.',
            'The titan depot, silicate vault and isotope tank set the ceiling for their resource. Once a store is full, excess production simply runs off unused — the resource bar flags a store that\'s nearly full with its own colour.',
          ),
        ],
      },
      {
        id: 'growth',
        heading: lt('Warum jede Stufe teurer wird', 'Why every level costs more'),
        body: [
          lt(
            'Kosten und Bauzeit steigen mit jeder Stufe eines Gebäudes, jeder Forschung und jedem Schiff — anfangs schnell erschwinglich, später eine bewusste, planbare Entscheidung. Das ist Absicht: es gibt immer eine sinnvolle nächste Ausbaustufe, aber nie einen Punkt, an dem alles gleichzeitig maximiert werden kann.',
            'Cost and build time rise with every level of a building, every research, every ship — cheap and fast early, a deliberate, planned decision later. That\'s intentional: there\'s always a sensible next upgrade, but never a point where everything can be maxed out at once.',
          ),
        ],
      },
    ],
  },
  {
    id: 'buildings',
    title: lt('Gebäude', 'Buildings'),
    summary: lt(
      'Die Bauqueue, alle Gebäudetypen und wofür sie gut sind.',
      'The build queue, every building type and what it\'s for.',
    ),
    sections: [
      {
        id: 'queue',
        heading: lt('Die Bauqueue', 'The build queue'),
        body: [
          lt(
            'Pro Planet läuft ein Bauauftrag nach dem anderen — ein neuer Auftrag reiht sich hinten ein und startet automatisch, sobald der vorherige fertig ist. Die Gebäude-Ansicht zeigt bei jedem Gebäude die aktuelle Stufe und was die nächste Stufe zusätzlich bringt (Förderung, Energie, Lagerkapazität), bevor du sie in Auftrag gibst.',
            'One build job runs after another, per planet — a new job joins the back of the queue and starts automatically once the previous one finishes. The Buildings view shows each building\'s current level and exactly what the next level adds (output, power, storage) before you queue it.',
          ),
        ],
      },
      {
        id: 'catalogue',
        heading: lt('Die wichtigsten Gebäude', 'The key buildings'),
        body: [
          lt(
            'Titan-Schmelze, Silikat-Raffinerie und Isotop-Kondensator fördern die drei Grundrohstoffe. Solar-Gravitations-Kraftwerk liefert Energie. Titan-Depot, Silikat-Speicher und Isotop-Tank erhöhen die Lagerkapazität. Quanten-Forschungslabor schaltet Forschung frei und verkürzt ihre Dauer. Werft und Schiffshangar bauen Schiffe und bestimmen, wie viele Flotten gleichzeitig unterwegs sein können.',
            'The titan smelter, silicate refinery and isotope condenser mine the three base resources. The solar-gravitational plant supplies power. The titan depot, silicate vault and isotope tank raise storage capacity. The quantum research lab unlocks research and shortens its duration. The shipyard and ship hangar build ships and set how many fleets can be away at once.',
          ),
          lt(
            'Händler-Relaisstation schaltet den Händler frei, Casino das Casino (braucht zusätzlich die Relaisstation). Nano-Fabrikationszentrum verkürzt Bauzeiten. Interkontinental-Silobasis baut Abfangraketen gegen Kometen. Die Subraum-Phalanx steht auf deinem Mond und scannt Flottenbewegungen im Sektor.',
            'The trade relay unlocks the merchant, the casino unlocks the casino (also needs the relay). The nano-fabrication center shortens build times. The intercontinental missile silo builds interceptor missiles against comets. The subspace phalanx sits on your moon and scans fleet movements in the sector.',
          ),
        ],
      },
    ],
  },
  {
    id: 'research',
    title: lt('Forschung & Talente', 'Research & talents'),
    summary: lt(
      'Die globale Forschungswarteschlange und der Talentbaum mit drei Pfaden.',
      'The global research queue and the three-path talent tree.',
    ),
    sections: [
      {
        id: 'research-queue',
        heading: lt('Forschung', 'Research'),
        body: [
          lt(
            'Anders als Bau- und Werft-Warteschlangen ist die Forschungswarteschlange global für dein gesamtes Reich — nur eine Technologie wird gleichzeitig erforscht. Das Quanten-Forschungslabor verkürzt jede Forschungszeit; hat dein Reich mehrere Planeten, zählt das Labor auf jedem einzelnen mit.',
            'Unlike build and shipyard queues, the research queue is global for your whole empire — only one technology is researched at a time. The quantum research lab shortens every research duration; once your empire has multiple planets, the lab on each one counts toward it.',
          ),
          lt(
            'Forschung wirkt nicht nur direkt (schnellere Bauzeit, mehr Ladekapazität) — sie fließt auch in Kampf-, Ernte- und Spionageformeln ein, die in den jeweiligen Kapiteln beschrieben sind.',
            'Research doesn\'t just act directly (faster builds, more cargo) — it also feeds into combat, harvesting and espionage formulas, described in their own chapters.',
          ),
        ],
      },
      {
        id: 'talents',
        heading: lt('Talentbaum', 'Talent tree'),
        body: [
          lt(
            'Jeder Levelaufstieg gibt dir einen Talentpunkt. Der Talentbaum (in der Forschungsansicht) hat drei Pfade — Kampf, Wirtschaft und Logistik — mit gestaffelten Boni: je mehr Punkte in einem Pfad, desto stärker der Effekt (z. B. Schadensbonus, geringere Handelssteuer, günstigerer Treibstoffverbrauch). Punkte lassen sich frei über die drei Pfade verteilen.',
            'Every level-up grants you a talent point. The talent tree (inside Research) has three paths — combat, economy and logistics — with banded bonuses: the more points in a path, the stronger the effect (e.g. damage bonus, lower trade tax, cheaper fuel use). Points can be freely spread across the three paths.',
          ),
        ],
      },
    ],
  },
  {
    id: 'fleet',
    title: lt('Werft & Schiffe', 'Shipyard & ships'),
    summary: lt(
      'Schiffstypen, Geschwindigkeit und wie viele Flotten gleichzeitig unterwegs sein können.',
      'Ship types, speed, and how many fleets can be away at once.',
    ),
    sections: [
      {
        id: 'ship-types',
        heading: lt('Schiffstypen', 'Ship types'),
        body: [
          lt(
            'Aero-Sonde spioniert, Erz-Sammelschiff erntet Asteroiden und Trümmerfelder, Stellar-Transporter trägt Fracht. Vanguard-Korvette, Aegis-Kreuzer, Nova-Schlachtschiff und der seltene Void-Phantom sind Kampfschiffe mit steigender Stärke und steigenden Kosten. Mond-Gravitron-Brecher und Kolonieschiff sind Spezialschiffe für den Mondbau bzw. die Kolonisierung.',
            'The aero probe scouts, the ore collector harvests asteroids and debris fields, the stellar transporter carries cargo. The vanguard corvette, aegis cruiser, nova battleship and the rare void phantom are combat ships of rising strength and cost. The moon-gravitron breaker and the colony ship are special-purpose for moon founding and colonization.',
          ),
        ],
      },
      {
        id: 'speed',
        heading: lt('Geschwindigkeit & Reisezeit', 'Speed & travel time'),
        body: [
          lt(
            'Jeder Schiffstyp hat eine eigene Geschwindigkeit. Schickst du eine gemischte Flotte los, bestimmt das langsamste Schiff im Verband die Reisezeit — Sonden sind mit Abstand am schnellsten, schwere Schlachtschiffe am langsamsten. Die Flottenauswahl bei jedem Angriff/jeder Erntemission zeigt Reisezeit, Treibstoffkosten und Ladekapazität, bevor du bestätigst.',
            'Every ship type has its own speed. Send a mixed fleet, and the slowest ship in the group sets the travel time — probes are by far the fastest, heavy battleships the slowest. The fleet-selection panel on every attack/harvest mission shows travel time, fuel cost and cargo capacity before you confirm.',
          ),
        ],
        tips: [
          lt(
            'Ein reiner Sonden-Trupp zum Spähen ist immer schneller unterwegs als eine gemischte Kampfflotte zum selben Ziel.',
            'A pure scout squad is always faster than a mixed combat fleet to the same target.',
          ),
        ],
      },
      {
        id: 'capacity',
        heading: lt('Flottenkapazität', 'Fleet capacity'),
        body: [
          lt(
            'Werft und Schiffshangar bestimmen, wie viele Flotten gleichzeitig unterwegs sein können (Ernte, Lagerangriffe, PvP, Spionage zählen alle mit). Ist das Limit erreicht, bleiben weitere Schiffe im Hangar, bis eine laufende Mission zurückkehrt.',
            'The shipyard and ship hangar determine how many fleets can be away at the same time (harvesting, camp attacks, PvP, espionage all count). Once the limit is reached, further ships stay in the hangar until a running mission returns.',
          ),
        ],
      },
    ],
  },
  {
    id: 'defense',
    title: lt('Abwehr & Ereignisse', 'Defense & events'),
    summary: lt(
      'Verteidigungsanlagen, Kometen/Asteroiden/Raids und die automatische Abwehr im Offline-Fall.',
      'Defense installations, comets/asteroids/raids, and automatic offline defense.',
    ),
    sections: [
      {
        id: 'installations',
        heading: lt('Verteidigungsanlagen', 'Defense installations'),
        body: [
          lt(
            'Gauß-Geschütz und Plasma-Batterie sind stationäre Kampfeinheiten, die jeden Angriff auf deinen Planeten automatisch mitkämpfen — kein Klick nötig, sie stehen einfach bereit. Schildkuppel-Generator und Abfang-Raketensystem wehren speziell Kometeneinschläge ab (siehe unten).',
            'The Gauss cannon and plasma battery are stationary combat units that automatically join every battle on your planet — no click needed, they\'re simply there. The shield dome generator and interceptor missile system specifically defend against comet impacts (see below).',
          ),
        ],
      },
      {
        id: 'events',
        heading: lt('Sektor-Ereignisse', 'Sector events'),
        body: [
          lt(
            'In unregelmäßigen Abständen erscheint ein Ereignis: ein Erz-Asteroid (kann geerntet werden), ein Piratenüberfall auf dein System (dein Verteidigung kämpft automatisch mit) oder ein anfliegender Komet.',
            'At irregular intervals an event appears: an ore asteroid (can be harvested), a pirate raid on your system (your defense fights it automatically) or an incoming comet.',
          ),
          lt(
            'Bleibt ein Komet unbeantwortet, greift die automatische Abwehr: zuerst eine Abfangrakete (falls vorhanden), sonst eine Schildkuppel (falls vorhanden, wird dabei verbraucht), erst wenn beides fehlt, schlägt er ein und beschädigt ein bis zwei Gebäude — die dann bis zur Reparatur nicht mehr produzieren, dafür liefert der entstandene Krater 24 Stunden lang doppelten Silikat-Ertrag.',
            'If a comet goes unanswered, automatic defense kicks in: an interceptor missile first (if available), otherwise a shield dome (if available — this consumes it), and only if neither exists does it hit, damaging one or two buildings — which then stop producing until repaired, though the resulting crater yields double silicate output for 24 hours.',
          ),
        ],
        tips: [
          lt(
            'Schildkuppeln lassen sich beliebig oft bauen — jede fängt genau einen Einschlag ab und wird dann verbraucht, das ist also ein Puffer über mehrere Ereignisse, kein Dauerschutz.',
            'Shield domes can be built as many times as you like — each one absorbs exactly one impact and is then consumed, so it\'s a buffer over several events, not permanent protection.',
          ),
        ],
      },
    ],
  },
  {
    id: 'harvest',
    title: lt('Ernte-Missionen', 'Harvest missions'),
    summary: lt(
      'Asteroiden und Trümmerfelder mit Erz-Sammelschiffen abbauen.',
      'Mining asteroids and debris fields with ore collectors.',
    ),
    sections: [
      {
        id: 'how',
        heading: lt('Wie Ernten funktioniert', 'How harvesting works'),
        body: [
          lt(
            'Ein Erz-Asteroid taucht als Sektor-Ereignis auf, ein Trümmerfeld entsteht nach gewonnenen Schlachten (deinen eigenen wie fremden, je nach Ort). Schicke Erz-Sammelschiffe hin, sie laden Titan/Silikat entsprechend ihrer Ladekapazität und kehren nach der Reisezeit automatisch mit der Beute zurück.',
            'An ore asteroid appears as a sector event, a debris field forms after battles are won (your own and others\', depending on where). Send ore collectors there — they load titan/silicate up to their cargo capacity and automatically return with the loot after the travel time.',
          ),
        ],
      },
    ],
  },
  {
    id: 'camps',
    title: lt('Piratenlager', 'Pirate camps'),
    summary: lt(
      'Spähen, angreifen, Beute machen — drei Schwierigkeitsstufen im Heimatsystem.',
      'Scout, attack, loot — three difficulty tiers in your home system.',
    ),
    sections: [
      {
        id: 'tiers',
        heading: lt('Drei Lager, drei Stufen', 'Three camps, three tiers'),
        body: [
          lt(
            'In deinem Heimatsystem stehen bis zu drei Piratenlager, jedes in einer von drei Schwierigkeitsstufen — höhere Stufe heißt stärkere Garnison, aber auch mehr Beute und Erfahrung. Lager erneuern sich regelmäßig, sobald geplündert oder abgelaufen.',
            'Up to three pirate camps sit in your home system, each at one of three difficulty tiers — a higher tier means a stronger garrison, but also more loot and XP. Camps refresh regularly once looted or expired.',
          ),
        ],
      },
      {
        id: 'scout-attack',
        heading: lt('Spähen und angreifen', 'Scouting and attacking'),
        body: [
          lt(
            'Vor einem Angriff lohnt sich Spionage: Sonden schätzen die Erfolgschance anhand deiner Astrometrie-Forschung gegen die Tarnung des Lagers ein und melden bei Erfolg Garnison und Beute — bei Misserfolg werden die eingesetzten Sonden zerstört.',
            'Scouting pays off before an attack: probes estimate success chance from your astrometrics research against the camp\'s concealment, and on success report garrison and loot — on failure the probes sent are destroyed.',
          ),
          lt(
            'Angriffe laufen wie jede Flottenmission über die exakte Flottenauswahl mit Reisezeit- und Treibstoffvorschau. Bei einem Sieg fließt Beute (Titan/Silikat) heim, bei einer Niederlage kehren nur die Überlebenden zurück.',
            'Attacks run through the same exact fleet-selection panel as any fleet mission, with travel-time and fuel previews. A win brings loot (titan/silicate) home, a loss returns only the survivors.',
          ),
        ],
      },
    ],
  },
  {
    id: 'galaxy',
    title: lt('Galaxie & Kolonisierung', 'Galaxy & colonization'),
    summary: lt(
      'Ringe, Koordinaten, NPC-Kolonien und wie du weitere Planeten gründest.',
      'Rings, coordinates, NPC colonies, and how to found further planets.',
    ),
    sections: [
      {
        id: 'coordinates',
        heading: lt('Ringe, Systeme, Plätze', 'Rings, systems, slots'),
        body: [
          lt(
            'Die Galaxie ist in fünf Ringe geteilt (5 bis 1), jeder mit vielen Systemen zu je zehn Plätzen. Ring 5 ("Wiege der Pioniere") ist der Neulingsring, in dem jeder startet. Weiter innen (niedrigere Ringnummer) wird es riskanter und wertvoller — siehe Kapitel "PvP-Kampf" für die genauen Schutzregeln pro Ring.',
            'The galaxy splits into five rings (5 down to 1), each with many systems of ten slots. Ring 5 ("Cradle of Pioneers") is the newbie ring everyone starts in. Further in (lower ring number) gets riskier and more valuable — see the "PvP combat" chapter for the exact protection rules per ring.',
          ),
          lt(
            'Ein Platz in einem System ist entweder deine eigene Kolonie, ein anderer Spieler, eine NPC-Kolonie, ein zeitlich begrenztes Ereignis oder frei. Die Sektor-Karte zeigt dein Heimatsystem als 10er-Raster.',
            'A slot in a system is either your own colony, another player, an NPC colony, a time-limited event, or empty. The Sector Map shows your home system as a 10-slot grid.',
          ),
        ],
      },
      {
        id: 'npc',
        heading: lt('NPC-Kolonien', 'NPC colonies'),
        body: [
          lt(
            'NPC-Kolonien besetzen Plätze in der Galaxie und lassen sich wie Piratenlager spähen und angreifen — allerdings mit echten Koordinaten statt einer flachen Liste, und die Schwierigkeit richtet sich nach dem Ring, nicht nach deinem eigenen Level.',
            'NPC colonies occupy galaxy slots and can be scouted and attacked just like pirate camps — but with real coordinates instead of a flat list, and their difficulty scales with the ring, not your own level.',
          ),
        ],
      },
      {
        id: 'colonize',
        heading: lt('Kolonisierung', 'Colonization'),
        body: [
          lt(
            'Ab Ring 2 ("Schwerkraft-Grenze") und einem Mindestlevel kannst du mit einem Kolonieschiff einen freien Platz besiedeln — bis zu zehn Planeten insgesamt. Jeder Planet hat eigene Bau- und Werft-Warteschlangen; die Forschungswarteschlange bleibt für dein ganzes Reich gemeinsam, und das Quanten-Forschungslabor jedes Planeten verkürzt sie zusätzlich.',
            'From ring 2 ("Gravity Frontier") and a minimum level, you can settle an empty slot with a colony ship — up to ten planets total. Each planet has its own build and shipyard queues; the research queue stays shared across your whole empire, and every planet\'s quantum research lab shortens it further.',
          ),
        ],
      },
    ],
  },
  {
    id: 'pvp',
    title: lt('PvP-Kampf', 'PvP combat'),
    summary: lt(
      'Ring-Schutzregeln, wie eine Schlacht abläuft, und was danach passiert.',
      'Ring protection rules, how a battle resolves, and what happens after.',
    ),
    sections: [
      {
        id: 'protection',
        heading: lt('Schutzregeln pro Ring', 'Protection rules per ring'),
        body: [
          lt(
            'Ring 5 ("Wiege der Pioniere") ist Neulingsschutz: nur Spieler bis Level 15 dürfen dort überhaupt angreifen — erfahrene Spieler können dort niemanden farmen. Ring 4 und 3 ("Umkämpfter Sektor") erlauben Angriffe nur innerhalb eines Levelunterschieds von etwa 25 %. Ring 2 ("Schwerkraft-Grenze") ist weit offener, aber nicht grenzenlos. Ring 1 ("Der Brennpunkt") kennt kein Levellimit — freies PvP für alle, die sich dorthin wagen.',
            'Ring 5 ("Cradle of Pioneers") is newbie-protected: only players up to level 15 may attack there at all — experienced players can\'t farm anyone there. Ring 4 and 3 ("Contested Sector") only allow attacks within roughly a 25% level difference. Ring 2 ("Gravity Frontier") is much more open, but not unlimited. Ring 1 ("The Flashpoint") has no level cap at all — free-for-all PvP for anyone who ventures there.',
          ),
        ],
      },
      {
        id: 'battle',
        heading: lt('Wie eine Schlacht abläuft', 'How a battle resolves'),
        body: [
          lt(
            'Ein Angriff auf einen anderen Spieler ist eine echte Flottenmission mit Reisezeit nach Entfernung. Am Ziel wird die Schlacht serverseitig gegen den aktuellen Verteidiger-Spielstand ausgewertet — inklusive Bauaufträgen, die zwischenzeitlich fertig geworden wären, selbst wenn der Verteidiger offline war.',
            'An attack on another player is a real fleet mission with a distance-based travel time. On arrival, the battle resolves server-side against the defender\'s current state — including build jobs that would have completed in the meantime, even if the defender was offline.',
          ),
          lt(
            'Gewinnt der Verteidiger, hat der Angreifer verloren. Ein Sieg des Angreifers plündert Ressourcen; ein Teil der zerstörten Schiffe/Anlagen hinterlässt ein Trümmerfeld, das beide Seiten anschließend abernten können. Überlebende und Beute kehren erst nach der vollen Rückreise heim — die Flotte ist für die komplette Hin- und Rückstrecke unterwegs, nicht nur bis zum Kampf.',
            'If the defender wins, the attacker has lost. An attacker\'s win plunders resources; a share of destroyed ships/installations leaves a debris field either side can then harvest. Survivors and loot only come home after the full return leg — the fleet is away for the whole round trip, not just until the battle.',
          ),
          lt(
            'Beide Seiten bekommen einen Kampfbericht im Postfach.',
            'Both sides get a combat report in their mailbox.',
          ),
        ],
      },
    ],
  },
  {
    id: 'espionage',
    title: lt('Spionage & Gegenspionage', 'Espionage & counter-espionage'),
    summary: lt(
      'Was Sonden herausfinden, wann sie entdeckt werden, und was der Ausspähte davon mitbekommt.',
      'What probes find out, when they get spotted, and what the target learns about it.',
    ),
    sections: [
      {
        id: 'success',
        heading: lt('Erfolgschance', 'Success chance'),
        body: [
          lt(
            'Die Erfolgschance einer Spähmission vergleicht deine Astrometrie-Forschung mit der Sensor-Technologie des Ziels; jede zusätzlich eingesetzte Sonde erhöht die Chance weiter. Bei Erfolg siehst du Schiffe, Verteidigung und Ressourcen des Ziels. Bei Misserfolg werden die Sonden zerstört, ohne Informationen zu liefern.',
            'A scouting mission\'s success chance compares your astrometrics research against the target\'s sensor technology; every extra probe sent raises the chance further. On success you see the target\'s ships, defense and resources. On failure the probes are destroyed without delivering anything.',
          ),
        ],
      },
      {
        id: 'counter',
        heading: lt('Gegenspionage', 'Counter-espionage'),
        body: [
          lt(
            'Unabhängig vom Erfolg des Angreifers würfelt das Spiel zusätzlich, ob der Ausspähte den Versuch bemerkt — abhängig von dessen eigener Astrometrie-Forschung. Wird er bemerkt, bekommt er eine Nachricht mit dem Namen des Angreifers, sonst bleibt der Versuch unentdeckt. Ein hohes eigenes Forschungslevel ist also nicht nur Angriffs-, sondern auch Schutzwert.',
            'Independently of the attacker\'s success, the game separately rolls whether the target notices the attempt — based on the target\'s own astrometrics research. If noticed, they get a message with the attacker\'s name; otherwise the attempt goes unnoticed. High astrometrics is therefore both an offensive and a defensive stat.',
          ),
        ],
      },
      {
        id: 'from-report',
        heading: lt('Direkt aus dem Bericht angreifen', 'Attacking straight from the report'),
        body: [
          lt(
            'Ein erfolgreicher Spionagebericht im Postfach bietet einen Knopf "Von hier angreifen", der dich direkt zu den ausgespähten Koordinaten in der Galaxie-Ansicht bringt.',
            'A successful espionage report in your mailbox offers an "Attack from here" button that takes you straight to the scouted coordinates in the Galaxy view.',
          ),
        ],
      },
    ],
  },
  {
    id: 'trade',
    title: lt('Handel', 'Trading'),
    summary: lt(
      'Das Händler-Portal, Kurse, Steuern und Großhandelsangebote.',
      'The merchant portal, prices, taxes and bulk offers.',
    ),
    sections: [
      {
        id: 'basics',
        heading: lt('Kaufen und verkaufen', 'Buying and selling'),
        body: [
          lt(
            'Die Händler-Relaisstation (Gebäude) schaltet den Handel frei. Kaufen und Verkaufen von Titan, Silikat und Isotop läuft gegen Credits, jeweils mit einer Handelsmarge/-steuer in beide Richtungen — Logistik-Forschung und der Wirtschafts-Talentpfad senken diese Steuer.',
            'The trade relay (building) unlocks trading. Buying and selling titan, silicate and isotope runs against credits, each direction carrying a merchant margin/tax — logistics research and the economy talent path both reduce it.',
          ),
        ],
      },
      {
        id: 'offers',
        heading: lt('Großhandelsangebote', 'Bulk offers'),
        body: [
          lt(
            'Zusätzlich zum freien Kurs rotiert ein Brett mit befristeten Großhandelsangeboten — oft günstiger als der freie Kurs, aber begrenzt in Menge und Zeit.',
            'Alongside the free-floating price, a board of time-limited bulk offers rotates in — often cheaper than the free price, but limited in quantity and time.',
          ),
        ],
      },
    ],
  },
  {
    id: 'casino',
    title: lt('Casino', 'Casino'),
    summary: lt(
      'Slots, Roulette und High-Low — mit Tageslimit statt endlosem Risiko.',
      'Slots, roulette and high-low — with a daily cap instead of endless risk.',
    ),
    sections: [
      {
        id: 'games',
        heading: lt('Die Spiele', 'The games'),
        body: [
          lt(
            'Das Casino (Gebäude, braucht die Händler-Relaisstation) schaltet drei Spiele mit Credit-Einsatz frei: Spielautomaten, Roulette und High-Low. Alle sind auf eine Auszahlungsquote von rund 92 % kalibriert — auf lange Sicht ein Verlustgeschäft, aber mit echter Gewinnchance im Einzelspiel.',
            'The casino (building, needs the trade relay) unlocks three credit-stake games: slots, roulette and high-low. All are calibrated to roughly a 92% payout rate — a loss over the long run, but a real chance to win any single round.',
          ),
        ],
      },
      {
        id: 'limit',
        heading: lt('Tageslimit', 'Daily limit'),
        body: [
          lt(
            'Wie viel du pro Tag einsetzen darfst, richtet sich nach deinem Kommandanten-Level — ein höheres Level erlaubt höhere Einsätze. Das Limit erneuert sich alle 24 Stunden.',
            'How much you may stake per day scales with your commander level — a higher level allows bigger stakes. The limit resets every 24 hours.',
          ),
        ],
      },
    ],
  },
  {
    id: 'shop',
    title: lt('Chronos-Shop', 'Chronos shop'),
    summary: lt(
      'Die Premium-Währung, Bau-Boosts und das Tageslimit für Käufe.',
      'The premium currency, build boosts, and the daily purchase cap.',
    ),
    sections: [
      {
        id: 'chronos',
        heading: lt('Chronos-Kristalle', 'Chronos crystals'),
        body: [
          lt(
            'Chronos-Kristalle sind die einzige käufliche Währung im Shop — Rohstoffmengen und Bundles skalieren daraus, aber es gibt keinen direkten Rohstoffkauf gegen Echtgeld. Ein "Zeitsprung" verkürzt einen laufenden Bau-, Forschungs- oder Werftauftrag gegen Chronos-Kristalle.',
            'Chronos crystals are the shop\'s only purchasable currency — resource bundles scale from them, but there is no direct real-money resource purchase. A "time jump" shortens a running build, research or shipyard job in exchange for Chronos crystals.',
          ),
          lt(
            'Wie viele Chronos-Kristalle sich pro Tag kaufen lassen, ist gedeckelt — unabhängig vom Level, weil echtes Geld nicht wie Spielfortschritt mit dem Level wachsen soll.',
            'How many Chronos crystals can be bought per day is capped — independent of level, because real money isn\'t meant to scale with progress the way in-game currencies do.',
          ),
        ],
      },
    ],
  },
  {
    id: 'messages',
    title: lt('Nachrichten', 'Messages'),
    summary: lt(
      'Kampf-, Spionage- und private Nachrichten in einem Postfach.',
      'Combat, espionage and private messages in one mailbox.',
    ),
    sections: [
      {
        id: 'categories',
        heading: lt('Kategorien', 'Categories'),
        body: [
          lt(
            'Das Postfach sortiert nach Kategorie: Kampf (Angriffs- und Verteidigungsberichte), Spionage (eigene und fremde Spähversuche) und Privat (Nachrichten von anderen Spielern). Jeder Bericht hat einen kurzen Betreff und einen ausformulierten Text — Kampfberichte zeigen Verluste/Beute strukturiert, keine rohen Zahlenlisten.',
            'The mailbox sorts by category: combat (attack and defense reports), espionage (your own and others\' scouting attempts) and private (messages from other players). Every report has a short subject and a written-out body — combat reports show losses/loot in a structured way, not raw number dumps.',
          ),
        ],
      },
    ],
  },
  {
    id: 'moon',
    title: lt('Der Mond', 'The moon'),
    summary: lt(
      'Der unzerstörbare Basis-Mond und seine Sonderbauten.',
      'The indestructible home moon and its special buildings.',
    ),
    sections: [
      {
        id: 'phalanx',
        heading: lt('Subraum-Phalanx', 'Subspace phalanx'),
        body: [
          lt(
            'Jeder Spieler besitzt von Beginn an einen kleinen, unzerstörbaren Mond. Die Subraum-Phalanx lässt sich nur dort bauen und scannt Flottenbewegungen im Sektor — die Grundlage für Frühwarnung vor eingehenden Flotten. Der Mond taucht in der Gebäude-Ansicht als eigener Reiter auf.',
            'Every player owns a small, indestructible moon from the start. The subspace phalanx can only be built there and scans fleet movements in the sector — the basis for early warning about incoming fleets. The moon shows up as its own tab in the Buildings view.',
          ),
        ],
      },
    ],
  },
  {
    id: 'account',
    title: lt('Account & Einstellungen', 'Account & settings'),
    summary: lt(
      'Level/Erfahrung, Cloud-Speicherung, Theme, Bewegungsreduktion und Sprache.',
      'Level/XP, cloud saving, theme, reduced motion and language.',
    ),
    sections: [
      {
        id: 'level',
        heading: lt('Level & Erfahrung', 'Level & experience'),
        body: [
          lt(
            'Erfahrung sammelst du aus fast jeder Handlung — bauen, forschen, kämpfen, Kometen abwehren. Jeder Levelaufstieg gibt einen Talentpunkt und schaltet in Ringen mit Levelband neue PvP-Ziele frei.',
            'You gain XP from almost every action — building, researching, fighting, defending against comets. Every level-up grants a talent point and, in rings with a level band, unlocks new PvP targets.',
          ),
        ],
      },
      {
        id: 'sync',
        heading: lt('Cloud-Speicherung', 'Cloud saving'),
        body: [
          lt(
            'Dein Spielstand speichert laufend in der Cloud. Ein anonymer Zugang funktioniert sofort, lässt sich aber nur auf einem Gerät wiederfinden — verknüpfe einen Google-Account (in den Einstellungen oder über den Hinweis oben), um von jedem Gerät auf denselben Spielstand zuzugreifen.',
            'Your save persists to the cloud continuously. An anonymous account works immediately but can only be found again on one device — link a Google account (in Settings, or via the prompt at the top) to reach the same save from any device.',
          ),
        ],
      },
      {
        id: 'preferences',
        heading: lt('Anzeige-Einstellungen', 'Display preferences'),
        body: [
          lt(
            'In den Einstellungen lassen sich das Farbschema wechseln, Animationen reduzieren (für Bewegungsempfindlichkeit oder schwächere Geräte), die Bestätigung vor Zeitsprung-Käufen abschalten, die Sprache dieses Hilfe-Centers umstellen — und diese geführte Tour jederzeit erneut starten.',
            'Settings let you switch the colour theme, reduce animations (for motion sensitivity or weaker devices), turn off the confirmation before time-jump purchases, change this Help Center\'s language — and restart this guided tour at any time.',
          ),
        ],
      },
    ],
  },
];
